import type { IDocument } from "@bbj-types/document";
import type { INotifyByEmailModalForm, TCandidateData } from "@data-display/notifyCandidateModal/types";
import { UploadFileField } from "@inputs/file";
import { Alert, Button, Typography } from "@mui/material";
import { ErrorBoundary } from "@navigations/ErrorBoundary";
import { useGetCandidateDocumentsSuspense } from "@root/api/services/candidates/getDocuments";
import { type Dispatch, type SetStateAction, Suspense } from "react";
import { type Control, Controller } from "react-hook-form";

interface IAttachmentsWithCandidateDocumentsBaseProps {
  control: Control<INotifyByEmailModalForm, void>;
  setAttachmentsLoading: Dispatch<SetStateAction<boolean>>;
}

interface IAttachmentsWithCandidateDocumentsSuspenseProps extends IAttachmentsWithCandidateDocumentsBaseProps {
  getDocuments: () => Omit<IDocument, "author" | "metadata">[];
}

interface IAttachmentsWithCandidateDocumentsProps extends IAttachmentsWithCandidateDocumentsBaseProps {
  candidate: TCandidateData;
}

const AttachmentsWithCandidateDocumentsSuspense = ({
  control,
  setAttachmentsLoading,
  getDocuments,
}: IAttachmentsWithCandidateDocumentsSuspenseProps) => {
  const documents = getDocuments();

  return (
    <Controller
      control={control}
      name="attachments"
      render={({ field: { onChange } }) => (
        <UploadFileField
          selectableDocuments={documents}
          onChange={onChange}
          onLoading={setAttachmentsLoading}
          options={{ maxFiles: 5, maxFileSize: 26214400 }}
        />
      )}
    />
  );
};

export const AttachmentsWithCandidateDocuments = ({
  control,
  candidate,
  setAttachmentsLoading,
}: IAttachmentsWithCandidateDocumentsProps) => {
  const getDocuments = useGetCandidateDocumentsSuspense(candidate._id);

  return (
    <ErrorBoundary
      fallback={
        <Alert severity="error" sx={{ alignSelf: "start" }}>
          <Typography variant="body1SemiBold">Oops !</Typography>
          <Typography variant="body1">Une erreur inattendue est survenue</Typography>
        </Alert>
      }
    >
      <Suspense
        fallback={
          <Button variant="outlined" color="primary" size="small" loading disabled sx={{ alignSelf: "start" }}>
            Ajouter une pièce jointe
          </Button>
        }
      >
        <AttachmentsWithCandidateDocumentsSuspense
          control={control}
          setAttachmentsLoading={setAttachmentsLoading}
          getDocuments={getDocuments}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
